import { isPlatformBrowser } from '@angular/common';
import { Component, DOCUMENT, inject, NgZone, PLATFORM_ID } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, NavigationEnd, Router, RouterModule } from '@angular/router';

import { NgbRatingConfig } from '@ng-bootstrap/ng-bootstrap';
import { Actions, ofActionDispatched, ofActionSuccessful, Store } from '@ngxs/store';
import { Observable, filter } from 'rxjs';

import { LogoutAction, RestoreSessionAction } from './shared/action/auth.action';
import { IOption } from './shared/interface/theme-option.interface';
import { ThemeOptionState } from './shared/state/theme-option.state';
import { GetBranchAction } from './shared/action/cms.action';
import { AuthState } from './shared/state/auth.state';
import { fetchAuthSession } from 'aws-amplify/auth';
import { GetAppUserDetailsAction } from './shared/action/account.new.action';
import { AuthService } from './shared/services/auth.service';
import { SeoService } from './shared/services/seo.service';

@Component({
  selector: 'app-root',
  imports: [RouterModule],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App {
  private actions = inject(Actions);
  private router = inject(Router);
  private titleService = inject(Title);
  private ngZone = inject(NgZone);
  private meta = inject(Meta);
  private authService = inject(AuthService);
  private seoService = inject(SeoService);
  themeOption$: Observable<IOption> = inject(Store).select(
    ThemeOptionState.themeOptions,
  ) as Observable<IOption>;

  public favIcon: HTMLLinkElement | null;
  public isTabInFocus = true;
  public timeoutId: ReturnType<typeof setTimeout>;
  private currentMessageIndex = 0;
  private messages = ['⚡ Come Back !!', '🎉 Offers for you...'];
  private currentMessage: string;
  private delay = 1000; // Delay between messages in milliseconds
  
  constructor() {
    const document = inject<Document>(DOCUMENT);
    const config = inject(NgbRatingConfig);

    config.max = 5;
    config.readonly = true;

    this.themeOption$.subscribe(theme => {
      if (theme?.general?.mode === 'dark') {
        document
          .getElementsByTagName('html')[0]
          .classList.add(theme?.general && theme?.general?.mode);
      } else {
        document.getElementsByTagName('html')[0].classList.remove('dark');
      }

      // Set Direction
      if (theme?.general?.language_direction === 'rtl') {
        document.getElementsByTagName('html')[0].setAttribute('dir', 'rtl');
        document.body.classList.add('rtl');
      } else {
        document.getElementsByTagName('html')[0].removeAttribute('dir');
        document.body.classList.remove('rtl');
      }

      // Set Favicon
      this.favIcon = document.querySelector('#appIcon');
      this.favIcon!.href = theme?.logo?.favicon_icon?.original_url;

      // theme?.seo?.og_title &&
      //   this.meta.updateTag({ property: 'og:title', content: theme?.seo?.og_title });
      // theme?.seo?.og_description &&
      //   this.meta.updateTag({ property: 'og:description', content: theme?.seo?.og_description });
      // theme?.seo?.og_image?.original_url &&
      //   this.meta.updateTag({ property: 'og:image', content: theme?.seo?.og_image?.original_url });
      // theme?.seo?.meta_title &&
      //   this.meta.updateTag({ property: 'title', content: theme?.seo?.meta_title });
      // theme?.seo?.meta_description &&
      //   this.meta.updateTag({ property: 'description', content: theme?.seo?.meta_description });
      // theme?.seo?.meta_tags &&
      //   this.meta.updateTag({ property: 'keywords', content: theme?.seo?.meta_tags });

      // document.addEventListener('visibilitychange', () => {
      //   this.ngZone.run(() => {
      //     this.isTabInFocus = !document.hidden;
      //     if (this.isTabInFocus) {
      //       clearTimeout(this.timeoutId);
      //       // Set site title
      //       return this.titleService.setTitle(
      //         theme?.general?.site_title && theme?.general?.site_tagline
      //           ? `${theme?.general?.site_title} | ${theme?.general?.site_tagline}`
      //           : 'Deshi Authentic Products - Amardeshi',
      //       );
      //     } else {
      //       this.updateMessage();
      //     }
      //   });
      // });
    });

    // Private / utility routes (cart, checkout, account, auth, 404...) declare `data.privatePage`:
    // give them a proper title and keep them out of search results. Public pages set their own SEO.
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
      const privatePage = this.findPrivatePage(this.router.routerState.snapshot.root);
      if (privatePage) this.seoService.setPrivatePage(privatePage);
    });

    this.actions.pipe(ofActionSuccessful(LogoutAction)).subscribe(() => {
      void this.router.navigate(['/auth/login']);
    });
  }

  /** Deepest `privatePage` in the active route chain (child routes don't inherit parent data on their own) */
  private findPrivatePage(route: ActivatedRouteSnapshot | null): string | null {
    let found: string | null = null;
    for (; route; route = route.firstChild) {
      found = route.data?.['privatePage'] ?? found;
    }
    return found;
  }

  updateMessage() {
    // Clear the previous timeout
    clearTimeout(this.timeoutId);

    // Update the current message
    this.currentMessage = this.messages[this.currentMessageIndex];
    this.titleService.setTitle(this.currentMessage);
    // Increment the message index or reset it to 0 if it reaches the end
    this.currentMessageIndex = (this.currentMessageIndex + 1) % this.messages.length;

    // Set a new timeout to call the function again after the specified delay
    this.timeoutId = setTimeout(() => {
      this.updateMessage();
    }, this.delay);
  }

  ngOnDestroy() {
    // Clear the timeout when the component is destroyed
    clearTimeout(this.timeoutId);
  }

  store=inject(Store)
  private platformId = inject(PLATFORM_ID);
  async ngOnInit(): Promise<void> {
    // The Cognito session lives in browser storage (Amplify is configured in main.ts only),
    // so checking it during SSR can only fail and log the visitor out on the server.
    if (isPlatformBrowser(this.platformId)) {
      await this.checkSession();
    }
    this.store.dispatch(new GetBranchAction())
  }

  private async checkSession() {
    try {
    const session = await fetchAuthSession();

    if (session.tokens?.accessToken) {
      // ✅ Restore token to NGXS
      this.store.dispatch(new RestoreSessionAction({
        access_token: session.tokens.accessToken.toString(),
      }));

      // ✅ Also load user details — fixes null user on page refresh
      try {
        const currentUser = await this.authService.getUser();
        // landing pages (social-media/:slug) are guest-only: no customer to load there
        const isLandingPage = location.pathname.startsWith('/social-media/');
        if (currentUser?.userId && !isLandingPage) {
          this.store.dispatch(new GetAppUserDetailsAction(currentUser.userId));
        }
      } catch (e) {
        console.error('[App] Failed to load user details:', e);
      }
    }
  } catch {
    this.store.dispatch(new LogoutAction());
  }
  }
}
